import { useState, useEffect } from 'react';
import { apiFetch } from '../App';

export default function Bookmakers() {
  const [expanded, setExpanded] = useState(null);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    apiFetch('/promotions')
      .then(data => setPromotions(data.promotions || []))
      .catch(() => {});
  }, []);

  const getBookmakerPromos = (key) => promotions.filter(p => p.bookmaker === key || p.bookmaker === 'all');
  const BOOKMAKERS = [
    {
      key: 'betway_bw',
      name: 'Betway Botswana',
      url: 'https://www.betway.co.bw',
      color: '#00a651',
      markets: '200+',
      sports: 'Football, Tennis, Basketball, Rugby, Cricket, Boxing',
      note: 'Largest bookmaker in BW. Best football market coverage and competitive odds on major leagues.',
      tags: ['Mobile app', 'Live betting', 'Cash out'],
      oddsRating: 4.7,
      status: 'active',
    },
    {
      key: 'sunbet_bw',
      name: 'Sunbet Botswana',
      url: 'https://www.sunbet.co.bw',
      color: '#f59e0b',
      markets: '150+',
      sports: 'Football, Rugby, Cricket, Tennis, Golf',
      note: 'Strong rugby and cricket markets. Often has competitive odds on African football leagues.',
      tags: ['Mobile app', 'Live betting'],
      oddsRating: 4.4,
      status: 'active',
    },
    {
      key: 'pstbets_bw',
      name: 'PST Bets',
      url: 'https://www.pstbet.co.bw',
      color: '#1a56db',
      markets: '300+',
      sports: 'Football, Basketball, Tennis, Rugby, Cricket',
      note: 'Wide football coverage including lower leagues. Good source of value on less popular markets.',
      tags: ['Mobile app', 'Lucky 6'],
      oddsRating: 4.2,
      status: 'active',
    },
    {
      key: 'bbets_bw',
      name: 'BBets',
      url: 'https://www.bbets.co.bw',
      color: '#8b5cf6',
      markets: '100+',
      sports: 'Football, Tennis, Basketball',
      note: 'Powered by the Advbet platform. Frequently offers better odds than competitors on European football.',
      tags: ['Jackpot', 'Virtual games'],
      oddsRating: 4.3,
      status: 'active',
    },
    {
      key: 'bet267_bw',
      name: 'Bet267',
      url: 'https://www.bet267.co.bw',
      color: '#06b6d4',
      markets: '100+',
      sports: 'Football, Tennis, Basketball',
      note: 'Local BW bookmaker on the Advbet platform. Shares odds with BBets — useful for volume betting.',
      tags: ['Jackpot', 'Lucky numbers'],
      oddsRating: 4.2,
      status: 'active',
    },
    {
      key: 'betxplosion_bw',
      name: 'BetXplosion',
      url: 'https://www.betxplosion.com',
      color: '#ef4444',
      markets: '100+',
      sports: 'Football, Tennis, Basketball',
      note: 'Also on the Advbet platform. Occasionally prices independently from BBets and Bet267 — worth checking for arbs.',
      tags: ['Jackpot', 'Casino'],
      oddsRating: 4.1,
      status: 'active',
    },
  ];

  const PLATFORM_GROUPS = [
    {
      name: 'Independent platform',
      bookmakers: ['betway_bw', 'sunbet_bw', 'pstbets_bw'],
      desc: 'These bookmakers run their own proprietary odds systems and frequently price markets differently from each other — the best source of arbitrage and value opportunities.',
    },
    {
      name: 'Advbet platform',
      bookmakers: ['bbets_bw', 'bet267_bw', 'betxplosion_bw'],
      desc: 'These three bookmakers share the Advbet platform and often have similar odds. However, they occasionally price independently — always worth checking all three.',
    },
  ];

  function OddsBar({ rating }) {
    const color = rating >= 4.5 ? '#00e5b0' : rating >= 4.2 ? '#f59e0b' : '#a8b4c8';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${(rating / 5) * 100}%`, height: '100%', background: color, borderRadius: 2 }} />
        </div>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color, minWidth: 28 }}>{rating}</span>
      </div>
    );
  }

  const bkmByKey = Object.fromEntries(BOOKMAKERS.map(b => [b.key, b]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header info */}
      <div style={{
        background: 'rgba(0,229,176,0.04)', border: '1px solid rgba(0,229,176,0.15)',
        borderRadius: 'var(--radius)', padding: '16px 20px',
        display: 'flex', alignItems: 'flex-start', gap: 16,
      }}>
        <div style={{ fontSize: 24, flexShrink: 0 }}>🎯</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
            6 bookmakers tracked in real time
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>
            Odds are scraped directly from each bookmaker and updated every 2 minutes. OddsEdgeBW is not affiliated with any bookmaker — we display odds for comparison purposes only. Always gamble responsibly.
          </div>
        </div>
      </div>

      {/* Platform groups */}
      {PLATFORM_GROUPS.map(group => (
        <div key={group.name}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'var(--font-display)', marginBottom: 4 }}>
              {group.name}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 600 }}>{group.desc}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {group.bookmakers.map(key => {
              const bm = bkmByKey[key];
              if (!bm) return null;
              return (
                <div key={bm.key} className="card" style={{ padding: '20px 24px', cursor: 'pointer' }}
  onClick={() => setExpanded(expanded === bm.key ? null : bm.key)}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* Left — info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: bm.color, flexShrink: 0,
                        }} />
                        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17 }}>{bm.name}</span>
                        <span style={{
                          fontSize: 10, padding: '2px 8px',
                          background: 'rgba(18,201,142,0.1)', color: 'var(--success)',
                          border: '1px solid rgba(18,201,142,0.2)', borderRadius: 10,
                          fontFamily: 'var(--font-display)',
                        }}>● active</span>
                      </div>

                      <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 6 }}>
                        {bm.markets} markets · {bm.sports}
                      </div>

                      <div style={{ fontSize: 13, color: 'var(--text2)', fontStyle: 'italic', marginBottom: 12 }}>
                        {bm.note}
                      </div>

                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {bm.tags.map(tag => (
                          <span key={tag} className="pill pill-blue" style={{ fontSize: 10 }}>{tag}</span>
                        ))}
                      </div>
                    </div>

                    {/* Right — rating + visit */}
<div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
  <div>
    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Odds rating</div>
    <OddsBar rating={bm.oddsRating} />
  </div>
  <a
    href={bm.url}
    target="_blank"
    rel="noopener noreferrer"
    className="btn btn-outline"
    style={{ fontSize: 12, padding: '7px 16px', textDecoration: 'none', alignSelf: 'flex-start' }}
  >
    Visit {bm.name.split(' ')[0]} ↗
  </a>
</div>
</div>
                  {/* Promotions section — shows when expanded */}
                  {expanded === bm.key && (
                    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}
                      onClick={e => e.stopPropagation()}>
                      <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 12, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        🎁 Current Promotions
                      </div>
                      {getBookmakerPromos(bm.key).length === 0 ? (
                        <div style={{ fontSize: 13, color: 'var(--text3)' }}>No active promotions at the moment.</div>
                      ) : (
                        getBookmakerPromos(bm.key).map(promo => (
                          <div key={promo._id} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{promo.title}</div>
                              {promo.value && <span className="pill pill-green" style={{ fontSize: 10 }}>{promo.value}</span>}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8, lineHeight: 1.6 }}>{promo.description}</div>
                            {promo.howToUse && (
                              <div style={{ fontSize: 12, color: 'var(--text2)', background: 'rgba(0,229,176,0.06)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', lineHeight: 1.6 }}>
                                💡 <strong>How to use:</strong> {promo.howToUse}
                              </div>
                            )}
                            {promo.expiresAt && (
                              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                                Expires: {new Date(promo.expiresAt).toLocaleDateString('en-BW', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Tips */}
      <div className="card" style={{ padding: '20px 24px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
          💡 How to use multiple bookmakers
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(3,1fr)', gap: 16 }}>
          {[
            { title: 'Open accounts at all 6', desc: 'You need accounts at all bookmakers to take advantage of the best odds and arbitrage opportunities. Open them before you need them.' },
            { title: 'Keep funds spread across bookmakers', desc: 'Keep some balance at each bookmaker. When an opportunity appears you need to act fast — transferring funds takes time.' },
            { title: 'Watch for account limitations', desc: 'Bookmakers may limit winning accounts. Spread your bets across bookmakers and avoid always betting on the same outcomes.' },
          ].map(tip => (
            <div key={tip.title} style={{ padding: '14px 16px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{tip.title}</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.6 }}>{tip.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Responsible gambling note */}
      <div style={{
        fontSize: 12, color: 'var(--text3)', textAlign: 'center',
        padding: '12px 20px', lineHeight: 1.6,
      }}>
        ⚠️ Gambling should be entertainment. Never bet money you cannot afford to lose. National Responsible Gambling Programme: <strong style={{ color: 'var(--text2)' }}>0800 006 008</strong> (free, confidential).
      </div>
    </div>
  );
}